import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { useAuthContext, useHasPermission } from '../../contexts/auth';
import { useDemo } from '../../api/demo';
import { ERROR_SNACKBAR } from '../../consts';
import { pathAfterDemoLoad } from '../tutorial/tutorial.context';
import { ClearDemoModal } from './clearDemoModal.component';

/** How often the loading screen asks whether the demo is in yet. */
const POLL_INTERVAL_MS = 3000;

/**
 * Covers the app while the demo loads in the background (half a minute or so),
 * so nothing is entered into a workspace that is being filled. Polls the
 * lightweight demo endpoint rather than the current user, whose every refresh
 * sets off the effects that depend on it.
 */
const DemoLoadingScreen = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { query } = useDemo();

  useEffect(() => {
    const interval = setInterval(async () => {
      const state = await query();
      if (!state || state.status === 'L') return;

      clearInterval(interval);
      if (state.status === 'A') {
        // A full reload, so every page fetches the demo's records afresh -
        // carrying the tutorial along when this tab asked for the demo.
        window.location.assign(pathAfterDemoLoad());
        return;
      }
      enqueueSnackbar(t('Could not load the demo data. Please try again.'), ERROR_SNACKBAR);
      window.dispatchEvent(new Event('tenantUpdated'));
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [query, enqueueSnackbar, t]);

  return (
    <Dialog open disableEscapeKeyDown maxWidth="xs" fullWidth>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
          <CircularProgress />
          <Typography variant="subtitle1" align="center">
            {t('Loading demo data...')}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            {t('We are filling the workspace with a year of a made-up company’s history. It can take up to a minute.')}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Says, on every page, that the workspace holds demo data and that nothing in
 * it reaches the outside world - which is why a reminder or an invoice e-mail
 * to a client is refused (app.demo_mode).
 */
export const DemoBanner = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const canManage = useHasPermission('app.change_tenant');
  const [clearOpened, setClearOpened] = useState(false);

  const status = currentUser?.tenant?.demo_status;
  if (status === 'L') return <DemoLoadingScreen />;
  if (status !== 'A') return null;

  return (
    <Box>
      <Alert
        severity="info"
        // On a phone the button goes under the text rather than squeezing it
        // into a narrow column beside it.
        sx={{
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          '& .MuiAlert-message': { flex: 1, minWidth: 0 },
          '& .MuiAlert-action': {
            width: { xs: '100%', sm: 'auto' },
            justifyContent: 'flex-end',
            ml: { xs: 0, sm: 'auto' },
            pl: { xs: 0, sm: 2 },
            pt: { xs: 0, sm: 0.5 },
          },
        }}
        action={
          canManage && (
            <Button
              color="inherit"
              size="small"
              onClick={() => setClearOpened(true)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {t('Clear demo data')}
            </Button>
          )
        }
      >
        {t('This workspace holds demo data. E-mails go only to your workspace’s users, nothing is sent to KSeF and no courier is ordered.')}
      </Alert>
      <ClearDemoModal opened={clearOpened} setOpened={setClearOpened} />
    </Box>
  );
};
